import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import {
  CURRENT_PAGE_WORKING_ENVIRONMENT,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { WorkEnvironmentRatingQuestion } from '../../questions/RatingQuesions/WorkEnvironmentRatingQuestion';
import { WorkingEnvironmentDetailsQuestion } from '../../questions/SimpleQuestions/WorkingEnvironmentDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const WorkingEnvironmentPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_WORKING_ENVIRONMENT,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    workEnvironmentDescription: review?.workEnvironmentDescription,
  };

  const { workEnvironmentDescription } = validationRules(config.language);

  const rules = { workEnvironmentDescription } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_WORKING_ENVIRONMENT,
      variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_WORKING_ENVIRONMENT,
      variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    setter({
      ...review,
      workEnvironmentRating: undefined,
      workEnvironmentDescription: undefined,
    });

    setPage(ExperimentTwoProgress.SubmitSuccess);
  };

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Working environment</Heading>
        <Stack space="small">
          <WorkEnvironmentRatingQuestion
            onChange={handleOnChange('workEnvironmentRating')}
            value={review?.workEnvironmentRating}
            title="Rate the physical environment"
            isRequired={true}
          />
          <WorkingEnvironmentDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'workEnvironmentDescription',
              previousValue: review,
            })}
            value={review?.workEnvironmentDescription}
            tone={getTone('workEnvironmentDescription')}
            message={getMessage('workEnvironmentDescription')}
          />
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
